import React, { createContext, useContext } from "react";
import PropTypes from "prop-types";

import { useThemeA } from "../../internal/ThemeAContext";

// Une puce posée dans une piste reprend la logique du contrôle segmenté :
// piste teintée = rien de choisi, blanc en relief = choisi. Hors piste, la puce
// cochée conserve le fond du survol : la teinte que l'on voit en passant dessus
// reste une fois le choix fait, en appui de la pastille-coche et de la bordure.
const FilterChipTrackContext = createContext(false);

// Graisse et emplacement de la coche constants : cocher une puce ne déplace
// jamais ses voisines.
const BASE_CLASS = [
  "akv:inline-flex akv:min-h-[2.125rem] akv:box-border akv:appearance-none akv:items-center akv:justify-center akv:gap-1.5",
  "akv:rounded-full akv:border akv:border-solid akv:pr-3 akv:pl-2 akv:font-ak-body akv:text-[0.8125rem] akv:font-semibold",
  "akv:transition-colors akv:duration-150 akv:motion-reduce:transition-none akv:cursor-pointer",
  "akv:focus-visible:outline-[3px] akv:focus-visible:outline-offset-2 akv:focus-visible:outline-ak-focus",
  "akv:disabled:cursor-not-allowed akv:disabled:opacity-[0.58]",
].join(" ");

// Le survol et l'état coché lisent la même teinte, --ak-chip-hover : une puce qui
// porte une couleur de catégorie réagit dans CETTE couleur (fond teinté à 12 %,
// bordure de la couleur tant qu'elle n'est pas cochée) ; sans couleur, le repli
// est le thème. Survoler une puce cochée ne change donc plus son fond.
const STATE_CLASS = {
  plain: {
    false: "akv:border-ak-border-strong akv:bg-ak-surface akv:text-ak-muted akv:hover:bg-[var(--ak-chip-hover,var(--ak-v2-surface-subtle))] akv:hover:border-[var(--ak-chip-line,var(--ak-v2-border-strong))] akv:hover:text-ak-text",
    true: "akv:border-ak-brand akv:bg-[var(--ak-chip-hover,var(--ak-v2-surface-subtle))] akv:text-ak-text",
  },
  track: {
    false: "akv:border-transparent akv:bg-transparent akv:text-ak-muted akv:hover:bg-[var(--ak-chip-hover,var(--ak-v2-surface))] akv:hover:text-ak-text",
    true: "akv:border-ak-brand/20 akv:bg-ak-surface akv:text-ak-brand-hover akv:shadow-ak-surface akv:hover:bg-[var(--ak-chip-hover,var(--ak-v2-surface))]",
  },
};

const accentHoverStyle = (accent) => ({
  "--ak-chip-hover": `color-mix(in srgb, ${accent} 12%, var(--ak-v2-surface))`,
  ...(accentIsLight(accent) ? {} : { "--ak-chip-line": accent }),
});

const MARK_BASE = [
  "akv:box-border akv:inline-grid akv:size-4 akv:flex-none akv:place-items-center akv:rounded-full",
  "akv:border-[1.5px] akv:border-solid akv:text-[0.6875rem] akv:font-bold akv:leading-none",
].join(" ");

const MARK_CLASS = {
  off: "akv:border-ak-border-strong akv:bg-transparent akv:text-transparent",
  brand: "akv:border-ak-brand akv:bg-ak-brand akv:text-ak-brand-ink",
  accentDark: "akv:text-ak-brand-ink",
  accentLight: "akv:border-ak-text/35 akv:text-ak-text",
};

// Les couleurs de catégorie imposées vont du presque blanc au noir : l'encre de
// la coche suit la luminance. Une couleur non hexadécimale (variable CSS d'un
// jeton) est traitée comme foncée, ce que sont tous les jetons de collecte.
const accentIsLight = (accent) => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(accent).trim());
  if (!match) return false;
  const hex = match[1].length === 3 ? match[1].replace(/./g, "$&$&") : match[1];
  const [r, g, b] = [0, 2, 4].map((index) => {
    const channel = parseInt(hex.slice(index, index + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45;
};

// Décochée, l'anneau garde la couleur de la catégorie : le code couleur se lit
// avant toute sélection. Une couleur trop claire pour un trait garde l'anneau neutre.
const markAppearance = ({ accent, selected }) => {
  if (!selected) {
    return accent && !accentIsLight(accent)
      ? { className: MARK_CLASS.off, style: { borderColor: accent } }
      : { className: MARK_CLASS.off };
  }
  if (!accent) return { className: MARK_CLASS.brand };
  return accentIsLight(accent)
    ? { className: MARK_CLASS.accentLight, style: { backgroundColor: accent } }
    : { className: MARK_CLASS.accentDark, style: { backgroundColor: accent, borderColor: accent } };
};

export function FilterChip({ accent = null, children, disabled = false, onClick, selected = false }) {
  useThemeA("FilterChip");
  const surface = useContext(FilterChipTrackContext) ? "track" : "plain";
  const mark = markAppearance({ accent, selected });

  return (
    <button
      aria-pressed={selected}
      className={`${BASE_CLASS} ${STATE_CLASS[surface][String(selected)]}`}
      data-ak-v2-component="filter-chip"
      data-surface={surface}
      disabled={disabled}
      onClick={onClick}
      style={accent ? accentHoverStyle(accent) : undefined}
      type="button"
    >
      <span
        aria-hidden="true"
        className={`${MARK_BASE} ${mark.className}`}
        data-ak-v2-component="filter-chip-mark"
        style={mark.style}
      >
        ✓
      </span>
      <span>{children}</span>
    </button>
  );
}

FilterChip.propTypes = {
  accent: PropTypes.string,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
};

const TRACK_CLASS = "akv:box-border akv:flex akv:max-w-full akv:min-w-0 akv:flex-wrap akv:gap-1 akv:rounded-ak-surface akv:border akv:border-solid akv:border-ak-border-strong akv:bg-ak-brand-soft akv:p-1";

export function FilterChipTrack({ ariaLabel = undefined, children }) {
  useThemeA("FilterChipTrack");

  return (
    <FilterChipTrackContext.Provider value>
      <div aria-label={ariaLabel} className={TRACK_CLASS} data-ak-v2-component="filter-chip-track" role={ariaLabel ? "group" : undefined}>
        {children}
      </div>
    </FilterChipTrackContext.Provider>
  );
}

FilterChipTrack.propTypes = {
  ariaLabel: PropTypes.string,
  children: PropTypes.node.isRequired,
};
